import React from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Newspaper, FileText, Users, Mail, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api, { getAuthUser } from '@/lib/api';

export default function AdminDashboard() {
  const [stats, setStats] = React.useState(null);
  const [recentEdicoes, setRecentEdicoes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const user = getAuthUser();

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, edicoesRes] = await Promise.all([
        api.get('/estatisticas'),
        api.get('/edicoes', { params: { limit: 5 } })
      ]);
      setStats(statsRes.data);
      setRecentEdicoes(edicoesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Welcome */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">
            Bem-vindo, {user?.nome?.split(' ')[0]}!
          </h1>
          <p className="text-gray-600 mt-1">
            Painel de administração do DOEM - Diário Oficial Eletrônico Municipal de Acaiaca
          </p>
        </div>

        {/* Stats Cards */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Edições Publicadas</p>
                      <p className="text-3xl font-bold text-gray-900">{stats?.total_edicoes_publicadas || 0}</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-full">
                      <Newspaper className="text-green-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Rascunhos</p>
                      <p className="text-3xl font-bold text-gray-900">{stats?.total_rascunhos || 0}</p>
                    </div>
                    <div className="p-3 bg-yellow-100 rounded-full">
                      <FileText className="text-yellow-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Publicações</p>
                      <p className="text-3xl font-bold text-gray-900">{stats?.total_publicacoes || 0}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-full">
                      <TrendingUp className="text-blue-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Por Categoria</p>
                      <div className="flex gap-2 mt-1">
                        <span className="text-xs bg-[#B91C1C] text-white px-2 py-0.5 rounded">
                          Ex: {stats?.por_categoria?.Executivo || 0}
                        </span>
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">
                          Leg: {stats?.por_categoria?.Legislativo || 0}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-full">
                      <Users className="text-purple-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Editions */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Edições Recentes</CardTitle>
              </CardHeader>
              <CardContent>
                {recentEdicoes.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nenhuma edição criada ainda.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Edição</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Data</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Publicações</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentEdicoes.map((ed) => (
                          <tr key={ed.edicao_id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">
                              Nº {ed.numero_edicao}/{ed.ano}
                            </td>
                            <td className="py-3 px-4 text-gray-600">
                              {formatDate(ed.data_publicacao)}
                            </td>
                            <td className="py-3 px-4 text-gray-600">
                              {ed.publicacoes?.length || 0}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                ed.status === 'publicado' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {ed.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-[#B91C1C] text-white">
                <CardContent className="pt-6">
                  <h3 className="font-heading font-bold text-xl mb-2">DOEM - Acaiaca</h3>
                  <p className="text-white/80 text-sm">
                    Diário Oficial Eletrônico Municipal de Acaiaca - MG.
                    Publicação oficial dos atos normativos e administrativos do município.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-[#1F2937] text-white">
                <CardContent className="pt-6">
                  <h3 className="font-heading font-bold text-xl mb-2">Contato</h3>
                  <p className="text-white/80 text-sm">
                    Praça Tancredo Neves, 35 - Centro<br />
                    Acaiaca - MG, CEP: 35.438-000<br />
                    Tel: (31) 3887-1650
                  </p>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
